import axios from 'axios';

const base = () => process.env.REACT_APP_API_URL ?? '';

export type Stage3AttestCohort =
	| 'all'
	| 'iiia_no_oral'
	| 'iiib_finalist'
	| 'iiib_no_oral'
	| 'laureate'
	| 'no_certificate_number'
	| 'manual_r1'
	| 'no_zip_excluded_from_cohorts';

export interface Stage3AttestSettings {
	/** Pisemny + ustny (IIIB z punktami, Laureaci, „Wszyscy” wg profilu) */
	exam_dates_line: string;
	/** Krótsza — IIIA oraz IIIB z 0 pkt ustnych (zapisane w WordPress, per edycja) */
	exam_dates_line_written_only?: string;
	issue_line: string;
	/** @deprecated nieużywane w szablonie small */
	body_paragraph?: string;
	number_width: number;
	ibl_contact_block: string;
	/** @deprecated nieużywane w szablonie small */
	olijpsp_header_title?: string;
	signatory_title: string;
	signatory_name: string;
	certificate_year: string;
	/** mm, 12–100 — światło przed dolną stopką (logotypy MEN/IBL) */
	stage3_sp_footer_logos_space_before_mm?: number;
	/** Opcjonalnie: kolumny Platnik* w szablonie importu XLS Elektronicznego Nadawcy (nadawca / płatnik). */
	en_xlsx_platnik_rodzaj?: string;
	en_xlsx_platnik_nazwa?: string;
	en_xlsx_platnik_nazwa_cd?: string;
	en_xlsx_platnik_ulica?: string;
	en_xlsx_platnik_dom?: string;
	en_xlsx_platnik_lokal?: string;
	en_xlsx_platnik_kod?: string;
	en_xlsx_platnik_miejscowosc?: string;
	en_xlsx_platnik_nip?: string;
	/** Notatka: docelowy folder na Google Drive dla nazw plików skanów (tylko informacja). */
	signed_scan_gdrive_folder_hint?: string;
	/** Konwersja etykiet EN → arkusz: przesunięcie siatki w dół (+) / w górę (−), mm (−20…40). */
	en_label_sheet_offset_top_mm?: number;
	/** Dodatkowy odstęp między rzędami etykiet na arkuszu, mm (0…10). */
	en_label_sheet_row_gap_extra_mm?: number;
	/** Szerokość podglądu PDF skanów (50–75% szerokości rzędu obok danych). */
	signed_scan_preview_width_pct?: number;
}

export interface Stage3AttestStudentRow {
	student_id: number;
	personal_title: string;
	first_name: string;
	/** Puste w API gdy w bazie brak lub „brak”. */
	second_name: string;
	last_name: string;
	district_name: string;
	final_title: string;
	certificate_number: number | null;
	birth_date_words: string;
	city_locative: string;
	/** Miasto z pola ucznia (mianownik jak w bazie) — podgląd źródła odmiany */
	birthday_place: string;
	/** Data urodzenia z tabeli students (Y-m-d), tylko podgląd */
	birthday_db?: string;
	/** Surowy łańcuch kolumny `students.birthday` jak z API (np. ISO z Z) — panel pokazuje go dosłownie, bez konwersji strefy */
	birthday_stored_raw?: string;
	/** Propozycja „słownej daty” z pola birthday — jak przy synchronizacji (nie zapis) */
	birth_date_words_suggested?: string;
	/** Propozycja miejscownika z miejsca urodzenia (morfologia) */
	city_locative_suggested?: string;
	/** Propozycja dopełniacza z nazwy szkoły w bazie */
	school_genitive_suggested?: string;
	/** Klasa z bazy ucznia (class / class_text) — podgląd */
	class_display_db?: string;
	/** Propozycja klasy rzymskiej jak przy synchronizacji */
	class_roman_suggested?: string;
	school_id: number | null;
	/** Nazwa szkoły z bazy (mianownik) */
	school_name_original: string;
	/** Miasto szkoły z tabeli schools (pole city) */
	school_city: string;
	school_genitive: string;
	born_form: string;
	took_part_form: string;
	obtained_form: string;
	title_declaration: string;
	lokata_text: string;
	class_roman: string;
	last_generated_variant: string;
	is_pan: boolean;
	/** Wyklucz z ZIP / PDF zbiorczego (np. odebrane osobiście) */
	exclude_from_bulk_export?: boolean;
	/** Kohorta „R1 — poprawki”: oznaczenie ręczne (mailing, skany, ponowny proces) */
	r1_manual?: boolean;
	/** Zapisany plik pociętego skanu (ścieżka względna do katalogu uploadów WP) */
	signed_scan_pdf_rel_path?: string | null;
	/** Nazwa pliku wysyłana / widoczna przy weryfikacji */
	signed_scan_filename?: string | null;
}

export const getStage3AttestSettings = async (token: string | null) => {
	const url = `${base()}/stage3-attestations/settings`;
	return axios.get<Stage3AttestSettings>(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const saveStage3AttestSettings = async (token: string | null, data: Partial<Stage3AttestSettings>) => {
	const url = `${base()}/stage3-attestations/settings`;
	return axios.post<Stage3AttestSettings>(url, data, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const getStage3AttestStudents = async (token: string | null, cohort: Stage3AttestCohort) => {
	const url = `${base()}/stage3-attestations/students`;
	return axios.get<{ students: Stage3AttestStudentRow[] }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

/** Pełny wiersz z propozycjami morfologicznymi (wolniejsze) — np. przed otwarciem edycji. */
export const getStage3AttestStudentDetail = async (token: string | null, studentId: number) => {
	const url = `${base()}/stage3-attestations/students/${studentId}`;
	return axios.get<{ ok: boolean; error?: string; student: Stage3AttestStudentRow }>(url, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const STAGE3_SYNC_TEXTS_BATCH_SIZE = 50;

/** Obszary synchronizacji (domyślnie wszystkie true) */
export interface Stage3SyncAreas {
	birth_dates?: boolean;
	birth_place?: boolean;
	school?: boolean;
	class?: boolean;
}

export interface Stage3SyncTextsResult {
	updated: number;
	errors?: { student_id: number; message: string }[];
	error?: string;
	message?: string;
	/** Liczba uczniów w kohortcie (wszystkie partie) */
	total?: number;
	/** Offset tej partii */
	offset?: number;
	limit?: number;
	batch_processed?: number;
	/** Ile rekordów przetworzono łącznie do końca tej partii (offset + batch) */
	processed_total?: number;
	done?: boolean;
}

export const syncStage3AttestTexts = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	offset = 0,
	limit = STAGE3_SYNC_TEXTS_BATCH_SIZE,
	syncAreas?: Stage3SyncAreas,
	fillMissingOnly = false,
) => {
	const url = `${base()}/stage3-attestations/sync-texts`;
	const body: Record<string, unknown> = { cohort, offset, limit };
	if (syncAreas) {
		body.sync_areas = syncAreas;
	}
	if (fillMissingOnly) {
		body.fill_missing_only = true;
	}
	return axios.post<Stage3SyncTextsResult>(url, body, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export interface Stage3SchoolLocationPreview {
	ok: boolean;
	error?: string;
	school_city?: string;
	city_locative?: string;
	preposition?: string;
	school_name?: string;
	school_id?: number;
}

export const getStage3SchoolLocationPreview = async (token: string | null, studentId: number) => {
	const url = `${base()}/stage3-attestations/school-location-preview`;
	return axios.get<Stage3SchoolLocationPreview>(url, {
		params: { student_id: studentId },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const appendStage3SchoolCity = async (token: string | null, studentId: number, cityLocative?: string | null) => {
	const url = `${base()}/stage3-attestations/append-school-city`;
	const body: Record<string, unknown> = { student_id: studentId };
	if (cityLocative != null && cityLocative.trim() !== '') {
		body.city_locative = cityLocative.trim();
	}
	return axios.post<{ ok: boolean; error?: string; student?: Stage3AttestStudentRow }>(url, body, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const updateStage3AttestRow = async (
	token: string | null,
	studentId: number,
	fields: Partial<
		Pick<
			Stage3AttestStudentRow,
			| 'birth_date_words'
			| 'city_locative'
			| 'school_genitive'
			| 'born_form'
			| 'took_part_form'
			| 'obtained_form'
			| 'title_declaration'
			| 'lokata_text'
			| 'class_roman'
		>
	> & {
		first_name?: string;
		last_name?: string;
		second_name?: string;
		exclude_from_bulk_export?: boolean;
		r1_manual?: boolean;
	},
) => {
	const url = `${base()}/stage3-attestations/row/${studentId}`;
	return axios.put<{ ok?: boolean; student?: Stage3AttestStudentRow; error?: string }>(url, fields, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const exportStage3AttestCsv = async (token: string | null, cohort: Stage3AttestCohort) => {
	const url = `${base()}/stage3-attestations/export-csv`;
	return axios.get<{ filename: string; file_base64: string }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export type Stage3ShippingPostEnParams = {
	en_format: 'S' | 'M' | 'L';
	en_masa_kg: number;
	en_kategoria: string;
	en_uwagi?: string;
	/** Plik dla EN: xls (Excel 97–2003) lub xml (IWD — wymaga numerów umowy). */
	en_file_format: 'xls' | 'xml';
	/** Wymagane przy en_file_format === 'xml' (numery z umowy w Elektronicznym Nadawcy). */
	iwd_symbol?: string;
	iwd_karta_umowy?: string;
	iwd_umowa?: string;
};

/** Tabela pól adresowych — import Elektroniczny Nadawca (POST: format, masa, kategoria, opcjonalny opis). */
export const exportStage3ShippingPostXlsx = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	params: Stage3ShippingPostEnParams,
) => {
	const url = `${base()}/stage3-attestations/export-shipping-post-xlsx`;
	const body: Record<string, unknown> = {
		cohort,
		en_format: params.en_format,
		en_masa_kg: params.en_masa_kg,
		en_kategoria: params.en_kategoria,
		en_uwagi: params.en_uwagi ?? '',
		en_file_format: params.en_file_format,
	};
	if (params.en_file_format === 'xml') {
		body.iwd_symbol = params.iwd_symbol ?? '';
		body.iwd_karta_umowy = params.iwd_karta_umowy ?? '';
		body.iwd_umowa = params.iwd_umowa ?? '';
	}
	return axios.post<{ filename: string; file_base64: string }>(url, body, {
		headers: { Authorization: `Bearer ${token}` },
	});
};

/** PDF z EN (jedna etykieta na stronę) → arkusz A4, siatka 105×57 mm (2×5). */
export const convertStage3EnLabelsPdfToSheet = async (token: string | null, fileBase64: string) => {
	const url = `${base()}/stage3-attestations/convert-en-labels-pdf`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ file_base64: fileBase64 },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

export const exportStage3AttestZip = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	mode: 'preview' | 'numbered',
) => {
	const url = `${base()}/stage3-attestations/export-zip`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ cohort, mode },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

/** Jeden plik PDF — wszystkie strony kohorty (do zbiorczego wydruku). */
export const exportStage3AttestMergedPdf = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	mode: 'preview' | 'numbered',
) => {
	const url = `${base()}/stage3-attestations/export-merged-pdf`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ cohort, mode },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

export interface Stage3AttestPreviewHtml {
	html: string;
	student_id: number;
	first_name: string;
	last_name: string;
	final_title: string;
	certificate_number?: number | null;
	/** true = w HTML widać nadany numer (jak PDF numerowany) */
	preview_showing_number?: boolean;
}

export const getStage3AttestPreviewHtml = async (
	token: string | null,
	studentId: number,
	cohort?: Stage3AttestCohort,
) => {
	const url = `${base()}/stage3-attestations/preview-html`;
	return axios.get<Stage3AttestPreviewHtml>(url, {
		params: { student_id: studentId, ...(cohort != null ? { cohort } : {}) },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const exportStage3AttestSinglePdf = async (
	token: string | null,
	studentId: number,
	mode: 'preview' | 'numbered',
	cohort?: Stage3AttestCohort,
) => {
	const url = `${base()}/stage3-attestations/preview-pdf`;
	return axios.get<{ filename: string; file_base64: string }>(url, {
		params: { student_id: studentId, mode, ...(cohort != null ? { cohort } : {}) },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export type Stage3SignedScanPlanStudent = {
	student_id: number;
	first_name: string;
	last_name: string;
	order_index: number;
};

export const getStage3SignedScanPlan = async (token: string | null, cohort: Stage3AttestCohort) => {
	const url = `${base()}/stage3-attestations/signed-scan/plan`;
	return axios.get<{ expected_pages: number; students: Stage3SignedScanPlanStudent[] }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export type Stage3SignedScanStoredItem = {
	student_id: number;
	first_name: string;
	last_name: string;
	filename: string | null;
	has_file: boolean;
};

export const getStage3SignedScanStoredOverview = async (token: string | null, cohort: Stage3AttestCohort) => {
	const url = `${base()}/stage3-attestations/signed-scan/stored-overview`;
	return axios.get<{ expected_pages: number; in_sync: boolean; items: Stage3SignedScanStoredItem[] }>(url, {
		params: { cohort },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export const getStage3SignedScanStoredSegment = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	index: number,
) => {
	const url = `${base()}/stage3-attestations/signed-scan/stored-segment`;
	return axios.get<Stage3SignedScanSegment>(url, {
		params: { cohort, index },
		headers: { Authorization: `Bearer ${token}` },
	});
};

export type Stage3SignedScanSegment = {
	student_id: number;
	first_name: string;
	last_name: string;
	filename: string;
	pdf_base64: string;
	/** Wypełniane przez API od wersji z metadanymi zaświadczenia. */
	certificate_number?: number | null;
	certificate_display?: string | null;
	birth_date_words?: string;
	second_name?: string;
	/** Rok w „nr/rok” (ustawienia zaświadczeń lub bieżący). */
	certificate_year?: string;
};

/** Jedna lub wiele składowych PDF; strony w kolejności plików, potem opcjonalnie odwrotnie (scan_order). */
export const postStage3SignedScanSplit = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	filesBase64: string[],
	scanOrder: 'forward' | 'reverse' = 'forward',
) => {
	const url = `${base()}/stage3-attestations/signed-scan/split`;
	return axios.post<{ segments: Stage3SignedScanSegment[] }>(
		url,
		{ cohort, files_base64: filesBase64, scan_order: scanOrder },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

export const prepareStage3SignedScanEmailCampaign = async (
	token: string | null,
	cohort: Stage3AttestCohort,
	name: string,
	subject: string,
	message: string,
) => {
	const url = `${base()}/stage3-attestations/signed-scan/prepare-email-campaign`;
	return axios.post<{ campaign_id: number }>(
		url,
		{ cohort, name, subject, message },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

/** ZIP wszystkich pociętych skanów zapisanych na serwerze dla kohorty (jak po „Podziel i zapisz”). */
export const exportStage3SignedScanZip = async (token: string | null, cohort: Stage3AttestCohort) => {
	const url = `${base()}/stage3-attestations/signed-scan/export-zip`;
	return axios.post<{ filename: string; file_base64: string }>(
		url,
		{ cohort },
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
};

export function downloadBase64File(fileBase64: string, filename: string, mimeType = 'application/octet-stream'): void {
	const binary = atob(fileBase64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	const blob = new Blob([bytes], { type: mimeType });
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	window.URL.revokeObjectURL(url);
}
